import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
    IReclassificationAccountRef,
    IReclassificationTransaction,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';
import { displayAccountNumber } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassAccountNumber';
import { keyOf } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassificationAccountKey';
import {
    ConflictGroup,
    findConflicts,
    groupConflictsByAccount,
} from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassificationEngine';
import { localizationKeys } from '../../../../../utils/localizationKeys';

type AccountLike = { accountNumber: string; name: string; uniqueAccountId?: string };

interface ConflictCardVM {
    accountRef: IReclassificationAccountRef;
    statement: ReclassificationStatement;
    transactions: IReclassificationTransaction[];
    expanded: boolean;
    totalAmount: number;
    /** Reassign target dropdown selection. */
    reassignTarget: string;
}

/**
 * Conflict resolution view: groups unresolvable transactions by their
 * missing account ref, with Delete / Reassign actions per group.
 */
@Component({
    selector: 'aumico-reclassification-conflicts',
    templateUrl: './reclassification-conflicts.component.html',
    styleUrls: ['./reclassification-conflicts.component.scss'],
})
export class ReclassificationConflictsComponent implements OnChanges {

    /**
     * REV-21798: account number as shown to the user. A system-created reclassification account
     * carries an internal `MAN-xxxxxxxx` identity that must never be surfaced — every user-facing
     * rendering shows plain `MAN`.
     */
    displayNumber(accountNumber: string | null | undefined): string {
        return displayAccountNumber(accountNumber);
    }
    readonly ReclassificationPeriod = ReclassificationPeriod;
    localizationKeys = localizationKeys;

    @Input() transactions: IReclassificationTransaction[] = [];
    @Input() accountsByStatement: Record<ReclassificationStatement, AccountLike[]> = {
        [ReclassificationStatement.BALANCE_SHEET]: [],
        [ReclassificationStatement.INCOME_STATEMENT]: [],
    };

    @Output() deleteByAccount = new EventEmitter<IReclassificationAccountRef>();
    @Output() reassignAccount = new EventEmitter<{
        oldRef: IReclassificationAccountRef;
        newRef: IReclassificationAccountRef;
    }>();
    @Output() backToTable = new EventEmitter<void>();

    cards: ConflictCardVM[] = [];

    // Localized texts
    title = '';
    description = '';
    deleteLabel = '';
    reassignLabel = '';
    backLabel = '';
    transactionsCountLabel = '';
    totalLabel = '';
    cyLabel = '';
    pyLabel = '';
    reassignPlaceholder = '';
    confirmDeleteText = '';

    constructor(private readonly i18n: I18NextService) { }

    ngOnChanges(_changes: SimpleChanges): void {
        const t = (k: string): string => this.i18n.t(k) as string;
        this.title = t('aumico_grouping_masking_reclass_conflicts_title');
        this.description = t('aumico_grouping_masking_reclass_conflicts_description');
        this.deleteLabel = t('aumico_grouping_masking_reclass_conflicts_delete');
        this.reassignLabel = t('aumico_grouping_masking_reclass_conflicts_reassign');
        this.backLabel = t('aumico_grouping_masking_reclass_conflicts_back');
        this.transactionsCountLabel = t('aumico_grouping_masking_reclass_conflicts_transactions');
        this.totalLabel = t('aumico_grouping_masking_reclass_conflicts_total');
        this.cyLabel = t('aumico_grouping_masking_reclass_conflicts_cy');
        this.pyLabel = t('aumico_grouping_masking_reclass_conflicts_py');
        this.reassignPlaceholder = t('aumico_grouping_masking_reclass_modal_target_placeholder');
        this.confirmDeleteText = t('aumico_grouping_masking_reclass_conflicts_confirm_delete');
        this.rebuildCards();
    }

    private rebuildCards(): void {
        // REV-20899: re-resolve conflicts here (same engine used by the
        // banner count via `computeConflictCount`) instead of trusting a
        // `t.hasConflict` flag on the transaction. The flag is intended to
        // be stamped by the backend / engine, but it's not consistently set
        // — when the banner counted via `findConflicts` and this view
        // filtered by `t.hasConflict`, the cards array could be empty even
        // though the banner said "N conflicts".
        const conflicts = findConflicts(this.transactions, this.accountsByStatement);
        const groups = groupConflictsByAccount(conflicts, this.accountsByStatement);
        // Preserve expanded state across rebuilds.
        const previous = new Map(this.cards.map((c) => [keyOf(c.accountRef) + '|' + c.statement, c.expanded]));
        this.cards = groups.map<ConflictCardVM>((g: ConflictGroup) => ({
            accountRef: g.accountRef,
            statement: g.statement,
            transactions: g.transactions,
            expanded: previous.get(keyOf(g.accountRef) + '|' + g.statement) ?? true,
            totalAmount: g.transactions.reduce((s, t) => s + t.amount, 0),
            reassignTarget: '',
        }));
    }

    /** Eligible reassign targets for a card: same-statement accounts, excluding the conflicted ref itself. */
    targetsForCard(card: ConflictCardVM): AccountLike[] {
        const candidates = this.accountsByStatement[card.statement] ?? [];
        return candidates.filter((a) => {
            // When both sides carry a uniqueAccountId, use UUID comparison exclusively.
            if (a.uniqueAccountId && card.accountRef.uniqueAccountId) {
                return a.uniqueAccountId !== card.accountRef.uniqueAccountId;
            }
            // Otherwise fall back to 2-part composite key.
            return !(a.accountNumber === card.accountRef.accountNumber && (a.name ?? '') === card.accountRef.name);
        });
    }

    toggleCard(card: ConflictCardVM): void {
        card.expanded = !card.expanded;
    }

    onDelete(card: ConflictCardVM): void {
        if (!window.confirm(this.confirmDeleteText)) return;
        this.deleteByAccount.emit({ ...card.accountRef });
    }

    onReassignSelectChange(card: ConflictCardVM, value: string): void {
        card.reassignTarget = value;
    }

    onReassignConfirm(card: ConflictCardVM): void {
        if (!card.reassignTarget) return;
        const targets = this.targetsForCard(card);
        const picked = targets.find((a) => this.optionKey(a) === card.reassignTarget);
        if (!picked) return;
        this.reassignAccount.emit({
            oldRef: { ...card.accountRef },
            newRef: {
                accountNumber: picked.accountNumber,
                name: picked.name,
                ...(picked.uniqueAccountId ? { uniqueAccountId: picked.uniqueAccountId } : {}),
            },
        });
    }

    onBack(): void {
        this.backToTable.emit();
    }

    optionKey(a: AccountLike): string {
        return a.uniqueAccountId
            ? `${a.accountNumber}|${a.name}|${a.uniqueAccountId}`
            : `${a.accountNumber}|${a.name}`;
    }

    cyCount(card: ConflictCardVM): number {
        return card.transactions.filter((t) => t.period === ReclassificationPeriod.CURRENT_YEAR).length;
    }

    pyCount(card: ConflictCardVM): number {
        return card.transactions.filter((t) => t.period === ReclassificationPeriod.PREVIOUS_YEAR).length;
    }
}
